import { useCallback, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { showToast } from '@/components/base/Toast';
import { uploadFile } from '@/api/file';
import { listRegionManagers, requestJoinRegion, getMyRegionAssignment, type BackendRegionManager, type MyRegionAssignment } from '@/api/region';
import { ApiError } from '@/api/client';

const STATUS_STYLE: Record<'PENDING' | 'APPROVED' | 'REJECTED', { label: string; cls: string }> = {
  PENDING: { label: 'Chờ Vùng trồng duyệt', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  APPROVED: { label: 'Đã tham gia', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  REJECTED: { label: 'Đã từ chối', cls: 'bg-red-50 text-red-700 border-red-200' },
};

/**
 * HTX's self-service "xin gia nhập Vùng trồng" — the pilot's HTX↔Antagri
 * onboarding step. Now reads its own current assignment via GET
 * /region/my-assignment on mount (Feedback 08 C3) instead of only ever
 * showing the outcome of a submission made in the same session.
 */
export default function HtxRegionPage() {
  const { user } = useAuth();
  const [managers, setManagers] = useState<BackendRegionManager[]>([]);
  const [managersLoaded, setManagersLoaded] = useState(false);
  const [managerUserId, setManagerUserId] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<{ status: 'PENDING' | 'APPROVED' | 'REJECTED'; managerName?: string } | null>(null);

  const loadManagers = useCallback(() => {
    listRegionManagers()
      .then((res) => setManagers(res.items))
      .catch((e) => showToast(e instanceof ApiError ? e.message : 'Không tải được danh sách Vùng trồng', 'error'))
      .finally(() => setManagersLoaded(true));
  }, []);

  useEffect(loadManagers, [loadManagers]);

  useEffect(() => {
    let cancelled = false;
    getMyRegionAssignment()
      .then((assignment: MyRegionAssignment | null) => {
        if (cancelled || !assignment) return;
        setResult({ status: assignment.status, managerName: assignment.manager?.fullName });
      })
      // Not fatal — the page still works as a fresh submission form.
      .catch(() => undefined);
    return () => { cancelled = true; };
  }, []);

  if (!user || user.role !== 'Hợp tác xã') {
    return <Navigate to="/dang-nhap" replace />;
  }

  async function submit() {
    if (!managerUserId) {
      showToast('Chọn một Vùng trồng để gửi yêu cầu', 'error');
      return;
    }
    setSubmitting(true);
    try {
      let attachmentFileId: string | undefined;
      if (file) {
        attachmentFileId = await uploadFile(file, 'CONTRACT_DOCUMENT', file.type || 'application/octet-stream');
      }
      await requestJoinRegion(managerUserId, attachmentFileId);
      const manager = managers.find((m) => m._id === managerUserId);
      setResult({ status: 'PENDING', managerName: manager?.fullName });
      showToast('Đã gửi yêu cầu tham gia Vùng trồng', 'success');
    } catch (e) {
      showToast(e instanceof ApiError ? e.message : 'Gửi yêu cầu thất bại', 'error');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="px-4 py-4 pb-4 space-y-4">
      <div>
        <h2 className="text-lg font-bold text-gray-900">Vùng trồng</h2>
        <p className="text-xs text-gray-500 mt-0.5">Xin gia nhập phạm vi quản lý của một Vùng trồng (vd. Antagri)</p>
      </div>

      {result && (
        <div className={`rounded-xl border p-4 ${STATUS_STYLE[result.status].cls}`}>
          <p className="text-sm font-semibold">{STATUS_STYLE[result.status].label}</p>
          {result.managerName && <p className="text-xs mt-0.5">Gửi tới: {result.managerName}</p>}
        </div>
      )}

      <div className="rounded-xl border border-gray-200 bg-white p-4 space-y-3">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Vùng trồng</label>
          <select
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
            value={managerUserId}
            onChange={(e) => setManagerUserId(e.target.value)}
          >
            <option value="">— Chọn Vùng trồng —</option>
            {managers.map((m) => (
              <option key={m._id} value={m._id}>
                {m.fullName} · {m.phone}
              </option>
            ))}
          </select>
          {/* Feedback 08 B3: an empty roster (no Vùng trồng account provisioned
              yet) used to look identical to a working, just-empty dropdown, and
              the submit button stayed clickable with nothing to select. */}
          {managersLoaded && managers.length === 0 && (
            <p className="mt-1.5 text-[11px] text-amber-600">Chưa có Vùng trồng nào trong hệ thống — liên hệ Agrida để được thiết lập trước khi gửi yêu cầu.</p>
          )}
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Hợp đồng bao tiêu (đính kèm)</label>
          <input
            type="file"
            accept="application/pdf,image/*"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            className="block w-full text-xs text-gray-600 file:mr-3 file:rounded-lg file:border-0 file:bg-agrida-50 file:px-3 file:py-2 file:text-xs file:font-medium file:text-agrida-700"
          />
          <p className="text-[11px] text-gray-400 mt-1">
            Đính kèm để Vùng trồng đối chiếu với hạn mức xin duyệt. Có thể bỏ trống và gửi bổ sung sau.
          </p>
        </div>

        <button
          type="button"
          disabled={submitting || managers.length === 0}
          onClick={submit}
          className="w-full rounded-lg bg-agrida-600 text-white text-sm font-semibold py-2.5 disabled:opacity-50"
        >
          {submitting ? 'Đang gửi...' : 'Gửi yêu cầu tham gia'}
        </button>
      </div>
    </div>
  );
}
